import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Search, Loader2, LogOut } from "lucide-react";
import api from "@/lib/api";
import { EventDialog } from "@/components/EventDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const ROUTE_OF = { lead: (id) => `/leads/${id}`, opportunity: (id) => `/deals/${id}`, universe: () => "/universe", contact: () => "/contacts" };

const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function EntityPicker({ open, onOpenChange, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ leads: [], opps: [], universe: [], contacts: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const [l, o, u, c] = await Promise.all([
          api.get("/leads", { params: { search: q, page_size: 6 } }),
          api.get("/opportunities", { params: { search: q } }),
          api.get("/master-data", { params: { q, limit: 10 } }).catch(() => ({ data: { items: [] } })),
          api.get("/contacts", { params: { search: q } }).catch(() => ({ data: [] })),
        ]);
        const oppItems = Array.isArray(o.data) ? o.data : o.data.items || [];
        const filt = q.trim()
          ? oppItems.filter((x) => (x.name + " " + (x.company || "")).toLowerCase().includes(q.trim().toLowerCase()))
          : oppItems;
        const conts = Array.isArray(c.data) ? c.data : c.data.items || [];
        setResults({
          leads: (l.data.items || []).slice(0, 6), opps: filt.slice(0, 6),
          universe: q.trim() ? (u.data.items || []).slice(0, 6) : [],
          contacts: q.trim() ? conts.slice(0, 6) : [],
        });
      } catch { setResults({ leads: [], opps: [], universe: [], contacts: [] }); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="event-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><MapPin className="h-4 w-4 text-brand" /> Check In — pick the record</DialogTitle>
          <DialogDescription>Pick a lead, opportunity, Universe company or contact you are visiting.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by project / company…" className="h-9 pl-9" autoFocus data-testid="event-picker-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-3">
          {loading && <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
          {!loading && results.leads.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Leads</p>
              {results.leads.map((l) => (
                <button key={l.id} onClick={() => onPick({ entityType: "lead", entityId: l.id, company: l.project_name || l.company })}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200" data-testid={`event-pick-${l.id}`}>
                  <p className="text-sm font-medium text-slate-800">{l.project_name || l.company}</p>
                  <p className="text-xs text-slate-400">{l.company}{l.branch ? ` · ${l.branch}` : ""} · {l.lead_status}</p>
                </button>
              ))}
            </div>
          )}
          {!loading && results.opps.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Opportunities</p>
              {results.opps.map((o) => (
                <button key={o.id} onClick={() => onPick({ entityType: "opportunity", entityId: o.id, company: o.name })}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200" data-testid={`event-pick-${o.id}`}>
                  <p className="text-sm font-medium text-slate-800">{o.name}</p>
                  <p className="text-xs text-slate-400">{o.company}{o.branch ? ` · ${o.branch}` : ""} · {o.stage}</p>
                </button>
              ))}
            </div>
          )}
          {!loading && results.universe.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Universe Companies</p>
              {results.universe.map((u) => (
                <button key={u.id} onClick={() => onPick({ entityType: "universe", entityId: u.id, company: u.company })}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200" data-testid={`event-pick-${u.id}`}>
                  <p className="text-sm font-medium text-slate-800">{u.company}</p>
                  <p className="text-xs text-slate-400">{u.location || "—"}{u.segment ? ` · ${u.segment}` : ""} · Universe</p>
                </button>
              ))}
            </div>
          )}
          {!loading && results.contacts.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Contacts</p>
              {results.contacts.map((c) => (
                <button key={c.id} onClick={() => onPick({ entityType: "contact", entityId: c.id, company: c.name })}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200" data-testid={`event-pick-${c.id}`}>
                  <p className="text-sm font-medium text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.company || "—"}{c.designation ? ` · ${c.designation}` : ""} · Contact</p>
                </button>
              ))}
            </div>
          )}
          {!loading && results.leads.length === 0 && results.opps.length === 0 && results.universe.length === 0 && results.contacts.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">No matching records.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function HeaderCheckIn() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [, tick] = useState(0);

  const { data: active } = useQuery({
    queryKey: ["active-event"],
    queryFn: async () => (await api.get("/visits/active")).data,
    refetchInterval: 60000,
  });
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["active-event"] });
    qc.invalidateQueries({ queryKey: ["visits"] });
  };

  if (active) {
    const mins = Math.max(0, Math.round((Date.now() - new Date(active.check_in_at).getTime()) / 60000));
    return (
      <>
        <button onClick={() => setCheckoutOpen(true)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          title={`Checked in at ${active.entity_name} — tap to check out`} data-testid="header-checkout-btn">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
          <span className="hidden sm:inline max-w-[140px] truncate">{active.entity_name || "Checked in"} · {fmtDur(mins)}</span>
          <LogOut className="h-4 w-4 sm:hidden" />
        </button>
        <EventDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} mode="checkout" event={active}
          onSaved={() => { refresh(); navigate((ROUTE_OF[active.entity_type] || (() => "/"))(active.entity_id)); }} />
      </>
    );
  }

  return (
    <>
      <button onClick={() => setPickerOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors"
        data-testid="header-checkin-btn">
        <MapPin className="h-4 w-4 text-brand" />
        <span className="hidden sm:inline">Check In</span>
      </button>
      <EntityPicker open={pickerOpen} onOpenChange={setPickerOpen}
        onPick={(t) => { setTarget(t); setPickerOpen(false); }} />
      <EventDialog open={!!target} onOpenChange={(v) => !v && setTarget(null)} mode="checkin"
        entityType={target?.entityType} entityId={target?.entityId} company={target?.company}
        onSaved={() => { refresh(); navigate((ROUTE_OF[target.entityType] || (() => "/"))(target.entityId)); setTarget(null); }} />
    </>
  );
}
